import { makeVar } from '@apollo/client';

import type { RedlineGroupStatus } from './redline-types';
import { SuggestionStatus } from '../../../../chat-types';
import {
  getIsRedlineEdit,
  getIsRedlineInsert,
  getIsRedlineRemove,
} from '../../../helpers/redlineCodeParser';
import type { ParsedRedlineItem } from '../../../helpers/redlineCodeParser';

type MessageId = string;

/**
 * Reactive variable storing each redline suggestion's status per message,
 * keyed by groupId. In-paragraph edit actions, inserts, and removals each
 * contribute entries, so the chat card reads its status chip by groupId.
 */
export const redlineStatusesByMessageVar = makeVar<Record<MessageId, RedlineGroupStatus[]>>({});

/**
 * Sets the redline statuses for a given message.
 *
 * Merges with existing statuses, preserving user-decision statuses (Accepted /
 * Rejected / Redlined) so a re-parse doesn't clobber what the user already
 * decided. Client-only transient statuses like DocumentNotInView are NOT
 * preserved — they get re-evaluated from the new inputs.
 */
export function setRedlineStatuses(messageId: MessageId, statuses: RedlineGroupStatus[]) {
  const existing = redlineStatusesByMessageVar();
  const existingMessageStatuses = existing[messageId] ?? [];

  const existingStatusByGroupId = new Map<string, SuggestionStatus>();
  for (const entry of existingMessageStatuses) {
    existingStatusByGroupId.set(entry.groupId, entry.status);
  }

  const mergedStatuses = statuses.map(entry => {
    const existingStatus = existingStatusByGroupId.get(entry.groupId);
    if (existingStatus && isUserDecisionStatus(existingStatus)) {
      return { ...entry, status: existingStatus };
    }
    return entry;
  });

  redlineStatusesByMessageVar({
    ...existing,
    [messageId]: mergedStatuses,
  });
}

export function isUserDecisionStatus(
  status: SuggestionStatus
): status is
  | typeof SuggestionStatus.Accepted
  | typeof SuggestionStatus.Rejected
  | typeof SuggestionStatus.Redlined {
  return (
    status === SuggestionStatus.Accepted ||
    status === SuggestionStatus.Rejected ||
    status === SuggestionStatus.Redlined
  );
}

/**
 * Computes the status for a redline suggestion based on server state.
 *
 * The server applies redline edits to the Slate document at generation time
 * (over collab/Yjs), so the client only needs to surface the persisted server
 * status and the document-in-view gate — the chat panel never owns whether the
 * edit is "still applicable" to the paragraph.
 */
export function computeRedlineActionStatus(
  serverStatus: SuggestionStatus | undefined,
  options: { isDocumentInView: boolean }
): SuggestionStatus {
  if (!options.isDocumentInView) {
    return SuggestionStatus.DocumentNotInView;
  }
  return serverStatus ?? SuggestionStatus.Pending;
}

/**
 * Builds the per-groupId status entries for one parsed redline item.
 *
 * In-paragraph edits contribute one entry per action; inserts and removals
 * contribute a single entry each; errors contribute none (they never landed
 * as a suggestion, so they carry no accept/reject status). `isDocumentInView`
 * is decided per item from its own documentId by the caller.
 */
export function buildRedlineStatusEntries(
  item: ParsedRedlineItem,
  options: {
    isDocumentInView: boolean;
    serverStatuses?: Record<string, SuggestionStatus>;
  }
): RedlineGroupStatus[] {
  const { isDocumentInView, serverStatuses = {} } = options;

  if (getIsRedlineInsert(item) || getIsRedlineRemove(item)) {
    return [
      {
        groupId: item.groupId,
        status: computeRedlineActionStatus(serverStatuses[item.groupId], { isDocumentInView }),
      },
    ];
  }

  if (getIsRedlineEdit(item)) {
    return item.actions.map(action => ({
      groupId: action.groupId,
      status: computeRedlineActionStatus(serverStatuses[action.groupId], { isDocumentInView }),
    }));
  }

  return [];
}
